import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, XIcon, ChevronRightIcon, Image as ImageIcon, FilterIcon } from 'lucide-react';
import { samplePromotionalGraphics } from '../data/samplePromotionalGraphics';
import { PromotionalGraphicProps } from '../components/Graphics/PromotionalGraphic';
import GraphicsLightbox from '../components/Graphics/GraphicsLightbox';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import SaveToFavorites from '../components/UI/SaveToFavorites';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
// Genre configuration for quick links
const genreTiles = [{
  name: 'Romance',
  slug: 'romance'
}, {
  name: 'Fantasy',
  slug: 'fantasy'
}, {
  name: 'Sci-Fi',
  slug: 'sci-fi'
}, {
  name: 'Mystery',
  slug: 'mystery'
}, {
  name: 'Thriller',
  slug: 'thriller'
}, {
  name: 'Horror',
  slug: 'horror'
}, {
  name: 'Historical',
  slug: 'historical-fiction'
}, {
  name: 'Contemporary',
  slug: 'contemporary'
}];
const BookGraphics: React.FC = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredGraphics, setFilteredGraphics] = useState(samplePromotionalGraphics);
  const [activeLightbox, setActiveLightbox] = useState<string | null>(null);
  // Sidebar state
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedAuthors, setSelectedAuthors] = useState<string[]>([]);
  // Derived filter options from data
  const allTypes = [...new Set(samplePromotionalGraphics.map(g => g.type).filter(Boolean))] as string[];
  const allGenres = [...new Set(samplePromotionalGraphics.flatMap(g => g.tags || []))].slice(0, 12);
  const allAuthors = [...new Set(samplePromotionalGraphics.map(g => g.authorName).filter(Boolean))] as string[];
  const activeFilterCount = selectedTypes.length + selectedGenres.length + selectedAuthors.length;
  const filterSections: FilterSection[] = [{
    id: 'type',
    title: 'Graphic Type',
    type: 'checkbox',
    options: allTypes.map(t => ({
      value: t,
      label: t
    })),
    value: selectedTypes,
    onChange: (val: string) => setSelectedTypes(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'genre',
    title: 'Genre / Tag',
    type: 'checkbox',
    options: allGenres.map(g => ({
      value: g,
      label: g
    })),
    value: selectedGenres,
    onChange: (val: string) => setSelectedGenres(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'author',
    title: 'Author',
    type: 'checkbox',
    options: allAuthors.map(a => ({
      value: a,
      label: a
    })),
    value: selectedAuthors,
    onChange: (val: string) => setSelectedAuthors(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }];
  const resetFilters = () => {
    setSelectedTypes([]);
    setSelectedGenres([]);
    setSelectedAuthors([]);
    setSearchQuery('');
  };
  // Filter graphics based on search query + sidebar filters
  useEffect(() => {
    let result = [...samplePromotionalGraphics];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(graphic => graphic.title?.toLowerCase().includes(query) || graphic.description?.toLowerCase().includes(query) || graphic.authorName?.toLowerCase().includes(query) || graphic.tags?.some(tag => tag.toLowerCase().includes(query)) || graphic.type?.toLowerCase().includes(query));
    }
    if (selectedTypes.length > 0) {
      result = result.filter(g => g.type && selectedTypes.includes(g.type));
    }
    if (selectedGenres.length > 0) {
      result = result.filter(g => g.tags?.some(tag => selectedGenres.includes(tag)));
    }
    if (selectedAuthors.length > 0) {
      result = result.filter(g => g.authorName && selectedAuthors.includes(g.authorName));
    }
    setFilteredGraphics(result);
  }, [searchQuery, selectedTypes, selectedGenres, selectedAuthors]);
  const handleGraphicClick = (id: string) => {
    setActiveLightbox(id);
  };
  const closeLightbox = () => {
    setActiveLightbox(null);
  };
  const activeGraphic = samplePromotionalGraphics.find(graphic => graphic.id === activeLightbox);
  // Distribute items evenly across columns using round-robin
  const distributeItems = (items: PromotionalGraphicProps[], colCount: number) => {
    const columns: PromotionalGraphicProps[][] = Array.from({
      length: colCount
    }, () => []);
    items.forEach((item, index) => {
      columns[index % colCount].push(item);
    });
    return columns;
  };
  const cols2 = useMemo(() => distributeItems(filteredGraphics, 2), [filteredGraphics]);
  const cols3 = useMemo(() => distributeItems(filteredGraphics, 3), [filteredGraphics]);
  const cols4 = useMemo(() => distributeItems(filteredGraphics, 4), [filteredGraphics]);
  const cols5 = useMemo(() => distributeItems(filteredGraphics, 5), [filteredGraphics]);
  const renderCard = (graphic: PromotionalGraphicProps) => <div key={graphic.id} className="mb-4" onClick={() => handleGraphicClick(graphic.id)} data-id="element-8411" data-mp="1q588awv2oyg7">
      <div className={`relative overflow-hidden rounded-xl shadow-sm hover:shadow-lg transition-all cursor-pointer group ${dk.card}`} data-id="element-8412" data-mp="2tfm1nfb8kbxx">
        <img src={graphic.imageUrl} alt={graphic.title || 'Book graphic'} className="w-full h-auto object-cover" data-id="element-8413" data-mp="337rsumtnypc2" />
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" data-id="element-8414" data-mp="2yai2n4yga9n6">
          <SaveToFavorites itemId={graphic.id} size="sm" data-id="element-8415" data-mp="25jzsjjzb7809" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end" data-id="element-8416" data-mp="25lug761yte81">
          <div className="p-4 w-full text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300" data-id="element-8417" data-mp="2fh4iluw9znmc">
            <h3 className="text-sm font-bold truncate" data-id="element-8418" data-mp="3v0irf3ytkq4h">{graphic.title}</h3>
            {graphic.authorName && <p className="text-xs opacity-90 mt-0.5" data-id="element-8419" data-mp="2b7kxz00l8j0">
                by {graphic.authorName}
              </p>}
            <div className="flex flex-wrap gap-1 mt-2" data-id="element-8420" data-mp="7akgr98v5pxq">
              {graphic.tags?.slice(0, 2).map(tag => <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-white/20 rounded-full backdrop-blur-sm" data-id="element-8421" data-mp="3ojpmfky88dj1">
                  {tag}
                </span>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
  const renderColumn = (column: PromotionalGraphicProps[], colIndex: number) => <div key={colIndex} className="flex flex-col" data-id="element-8422" data-mp="bauoorg7uqf8">
      {column.map(renderCard)}
    </div>;
  const renderGrid = (columns: PromotionalGraphicProps[][]) => <>{columns.map((col, i) => renderColumn(col, i))}</>;
  return <div className={`min-h-screen ${dk.page}`} data-id="element-8423" data-mp="13z8j6x83j4m1">
      {/* Header Section */}
      <div className={`py-10 ${dk.surface} border-b ${dk.border}`} data-id="element-8424" data-mp="18s1qnsuy8r1t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-8425" data-mp="34uy5lhpum416">
          <div className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-8426" data-mp="34w98lfbgz7r8">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-8427" data-mp="302epaczs0uso">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-8428" data-mp="2r3t0i03pojjg" />
            <span className={dk.textPrimary} data-id="element-8429" data-mp="2r16rdw169vz4">Book Graphics</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-id="element-8430" data-mp="3nshe8k8e9kn2">
            <div className="flex items-center gap-3" data-id="element-8431" data-mp="3js71moq3jvz8">
              <ImageIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-8432" data-mp="3es2qzfbvdvgx" />
              <div data-id="element-8433" data-mp="3euf6zmy2hvrc">
                <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-8434" data-mp="39trh54lkrkyp">
                  Book Graphics Gallery
                </h1>
                <p className={`mt-1 ${dk.textSecondary}`} data-id="element-8435" data-mp="23egvzdjbwznh">
                  Explore visual teasers, character art, and trope graphics
                </p>
              </div>
            </div>
            <div className="w-full md:max-w-md relative" data-id="element-8436" data-mp="1obgqo9s1romk">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8437" data-mp="1t9i8yshnfjwz">
                <SearchIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-8438" data-mp="1y8lf6g9d7w6j" />
              </div>
              <input type="text" className={`block w-full pl-10 pr-10 py-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${dk.input}`} placeholder="Search graphics..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-8439" data-mp="1y6qsoxig3w0c" />
              {searchQuery && <button className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setSearchQuery('')} data-id="element-8440" data-mp="2gx8jgwjpwhj2">
                  <XIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-8441" data-mp="mmwhwfwna6fj" />
                </button>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-8442" data-mp="5z4dss8u6hdd">
        {/* Mobile Filter Button */}
        <div className="mb-6 lg:hidden" data-id="element-8443" data-mp="awntlwl6n27f">
          <button onClick={() => setMobileFiltersOpen(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border shadow-sm ${dk.card} ${dk.textSecondary}`} data-id="element-8444" data-mp="azjp7e9k5esp">
            <FilterIcon className="h-4 w-4" data-id="element-8445" data-mp="jw14mtoyfyu9" />
            Filters
            {activeFilterCount > 0 && <span className={`ml-1 px-1.5 py-0.5 ${rt.badgeRadius} text-xs font-bold text-white ${rt.bgDark}`} data-id="element-8446" data-mp="ovdwxtylhy48">
                {activeFilterCount}
              </span>}
          </button>
        </div>

        <div className="flex gap-8" data-id="element-8447" data-mp="12nk4irnomws2">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-8448" data-mp="17nydcbx3yar5">
            <FilterSidebar sections={filterSections} onReset={resetFilters} data-id="element-8449" data-mp="1ms159beoj226" />
          </FilterSidebarWrapper>

          {/* Gallery */}
          <div className="flex-1 min-w-0" data-id="element-8450" data-mp="1csjy0pjs4g43">
            {filteredGraphics.length > 0 ? <>
                {/* 2 columns: mobile to sm */}
                <div className="grid grid-cols-2 gap-4 md:hidden" data-id="element-8451" data-mp="18sjckky9j4nj">
                  {renderGrid(cols2)}
                </div>
                {/* 3 columns: md */}
                <div className="hidden md:grid md:grid-cols-3 lg:hidden gap-4" data-id="element-8452" data-mp="20husjpajde9s">
                  {renderGrid(cols3)}
                </div>
                {/* 4 columns: lg */}
                <div className="hidden lg:grid lg:grid-cols-4 xl:hidden gap-4" data-id="element-8453" data-mp="1rq2mrrwgitbj">
                  {renderGrid(cols4)}
                </div>
                {/* 5 columns: xl+ */}
                <div className="hidden xl:grid xl:grid-cols-5 gap-4" data-id="element-8454" data-mp="2o9jsnrtln71a">
                  {renderGrid(cols5)}
                </div>
              </> : <div className={`text-center py-16 rounded-xl shadow-sm ${dk.card}`} data-id="element-8455" data-mp="hkrhwl7vgcp2">
                <ImageIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-8456" data-mp="mhrnyw4q1fg6" />
                <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-8457" data-mp="7rsu35k78q6i">
                  No graphics found
                </h3>
                <p className={`mb-6 ${dk.textSecondary}`} data-id="element-8458" data-mp="3uxtjp9ine8bl">
                  We couldn't find any graphics matching your search.
                </p>
                <button onClick={resetFilters} className={`font-medium ${rt.accentLink}`} data-id="element-8459" data-mp="3td0ujxiylkn">
                  Clear filters
                </button>
              </div>}

            {/* Create Graphics CTA */}
            <div className="mt-16 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl p-8 md:p-12 text-white shadow-xl relative overflow-hidden" data-id="element-8460" data-mp="1ja3pdys648ql">
              <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" data-id="element-8461" data-mp="38pxm0p2je7j6"></div>
              <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-indigo-500/20 rounded-full blur-3xl" data-id="element-8462" data-mp="33rcst9lpco48"></div>
              <div className="relative z-10 text-center md:text-left md:flex md:items-center md:justify-between gap-8" data-id="element-8463" data-mp="3igisszsd6id6">
                <div data-id="element-8464" data-mp="3dnpoue4pze25">
                  <h2 className="text-2xl md:text-3xl font-bold mb-3" data-id="element-8465" data-mp="3dn7l6wfq9xk7">
                    Are you an author?
                  </h2>
                  <p className="text-indigo-100 max-w-xl text-lg" data-id="element-8466" data-mp="3nflmxp6t346d">
                    Create and share your own book graphics to help readers
                    discover the tropes, themes, and characters in your stories.
                  </p>
                </div>
                <button className="mt-6 md:mt-0 bg-white text-indigo-700 hover:bg-indigo-50 font-bold px-8 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 whitespace-nowrap" data-id="element-8467" data-mp="270kd4d2oky2z">
                  Create Book Graphics
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {activeGraphic && <GraphicsLightbox graphic={activeGraphic} onClose={closeLightbox} data-id="element-8468" data-mp="28ju1ipj3sltb" />}
    </div>;
};
export default BookGraphics;